import GitErrorCode from "@ext/git/core/GitCommands/errors/model/GitErrorCode";

export const JSErrorClass = 100;

/** libgit2 `GIT_TIMEOUT` (-37); `Error::code()` in `crates/git/src/error.rs` reports the absolute value. */
const GIT_TIMEOUT = 37;

/** Rust subset for `Error::Network` — see `crates/git/src/error.rs`. */
const NETWORK_SUBSET = 5;

/** Rust subset for `Error::Lfs` — see `crates/git/src/error.rs`. It carries no class and no code. */
const LFS_SUBSET = 2;

/**
 * git2-lfs `RemoteError::AccessDenied`: the LFS server answered 401 or 403 (`git2-lfs/src/remote/reqwest.rs`;
 * in wasm, `handle_http_error` in `crates/git/src/ext/lfs.rs`).
 */
const LFS_ACCESS_DENIED = "remote: access denied";

/** libgit2's error classes (`git_error_t`) and codes (`git_error_code`, as their absolute value). */
const GIT_CLASS_OS = 2;
const GIT_CLASS_NET = 12;
const GIT_CLASS_SSL = 16;
const GIT_CLASS_HTTP = 34;
const GIT_CERTIFICATE = 17;

/**
 * How libgit2's transports word a server they cannot reach, the system's own words included — refused, reset or
 * timed-out connections, names that do not resolve, TLS and certificate failures. Lower case; see
 * `libgit2/src/libgit2/streams/{socket,openssl}.c` and `transports/winhttp.c`.
 */
const NETWORK_WORDING = [
	"failed to connect",
	"failed to resolve address",
	"could not resolve",
	"failed to send request",
	"failed to receive response",
	"error receiving data from socket",
	"could not read from socket",
	"could not write to socket",
	"timed out",
	"connection reset",
	"reset by peer",
	"connection refused",
	"network is unreachable",
	"no route to host",
	"could not be established",
	"terminated abnormally",
	"broken pipe",
	"ssl error",
	"ssl certificate",
	"security error",
	"certificate",
];

/** The reason alone, without the call arguments, which name paths of any wording. A lock file is never the network. */
const isNetworkWording = (message: string, anyConnection: boolean): boolean => {
	const reason = message.split("\nArgs:")[0].toLowerCase();
	if (reason.includes("lock file") || reason.includes("failed to lock")) return false;
	if (anyConnection && reason.includes("connection")) return true;
	return NETWORK_WORDING.some((words) => reason.includes(words));
};

/** Rust subset for `Error::BranchCheckedOutElsewhere` — see `crates/git/src/error.rs`. */
const BRANCH_CHECKED_OUT_ELSEWHERE_SUBSET = 12;

/**
 * Synthetic status the wasm http layer records when the request never reached the server —
 * XHR reports status 0 there, which means nothing downstream. See
 * `apps/web/crates/gramax-wasm/js/httpError.ts`.
 */
const UNREACHABLE_STATUS = 999;

// libgit2 error classes, as serialized by crates/git/src/error.rs (git2::ErrorClass as u32).
const OsErrorClass = 2;
const NetErrorClass = 12;

// Class Net is a network error by definition, so any of these fragments in its message is enough.
const NET_FAILURE_MESSAGES = [
	"failed to resolve address",
	"could not resolve",
	"failed to connect",
	"failed to send request",
	"failed to receive response",
	"timed out",
	"connection",
];

// libgit2's WinHTTP transport starts the message with one of these prefixes and only then appends
// the message the operating system gave it — and that half comes in the user's locale ("failed to
// send request: Не удается разрешить имя или адрес сервера"). Matching the prefix keeps the check
// locale-independent.
const OS_TRANSPORT_PREFIXES = [
	"failed to resolve address",
	"failed to connect",
	"failed to send request",
	"failed to receive response",
	"failed to read data:",
	"failed to write data:",
	"failed to write chunk",
	"failed to write final chunk:",
];

// Callers append the call arguments to the libgit2 text (`\nArgs: {...}`, see
// app/resolveModule/rustcall/tauri.ts), and those carry repository paths and commit messages.
const libgit2Text = (message: string) => (message ?? "").split("\nArgs:")[0].trimStart();

// A failed request does not arrive under a single class: the libcurl/OpenSSL transports tag it
// GIT_ERROR_NET, while the WinHTTP transport used by Windows builds tags the very same failure
// GIT_ERROR_OS — `git (fetch, 1, 2, 1)` in the report. GIT_ERROR_OS also carries filesystem
// errors, so for it only a transport prefix at the start of libgit2's own text counts: a path or
// a commit message that merely mentions "connection" must not turn a disk error into "no network".
const isTransportFailure = (klass: number, message: string) => {
	if (klass === NetErrorClass) return NET_FAILURE_MESSAGES.some((failure) => message?.includes(failure));
	if (klass === OsErrorClass) {
		const text = libgit2Text(message);
		return OS_TRANSPORT_PREFIXES.some((prefix) => text.startsWith(prefix));
	}
	return false;
};

export class LibGit2Error extends Error {
	code?: GitErrorCode;
	/** libgit2's error class (`git_error_t`), which tells a failure on the way to the server from a local one. */
	klass?: number;
	data?: { [key: string]: string };
	command?: string;
	/** libgit2's own text: `message` also carries the call arguments (paths, author) for diagnostics. */
	reason: string;

	constructor(
		name: string,
		reason: string,
		subset: number,
		klass: number,
		code: number,
		command?: string,
		args?: unknown,
	) {
		const message = args === undefined ? reason : `${reason}\nArgs: ${JSON.stringify(args, null, 4)}`;
		super(message);
		this.name = name;
		this.reason = reason;
		this.command = command;
		this.klass = klass;
		this.code = fromRaw(subset, klass, code, message, command);
		this.data = makeData(this.code, code, reason);
	}
}

export interface PushDeclinedByRemoteData {
	/** libgit2 status line, e.g. `failed to push; refname: refs/heads/main; status: pre-receive hook declined`. */
	status: string;
	/** What the server printed over the sideband channel (hook output); absent when it printed nothing. */
	remoteMessage?: string;
}

/**
 * Rust appends the server's sideband output to the libgit2 message after a blank line —
 * see `RemoteMessages::attach` in `crates/git/src/remote_message.rs`.
 */
export const splitRemoteMessage = (message: string): PushDeclinedByRemoteData => {
	const [status, ...rest] = message.split("\n\n");
	const remoteMessage = rest.join("\n\n").trim();
	return { status: status.trim(), remoteMessage: remoteMessage || undefined };
};

export const fromRaw = (
	subset: number,
	klass: number,
	code: number,
	message: string,
	command?: string,
): GitErrorCode => {
	const eq = (targetKlass: number, targetCode: number) => targetKlass === klass && targetCode === code;

	switch (true) {
		// A full disk surfaces as a Rust io error whose Debug output carries `kind: StorageFull`
		// (Unix strerror: "No space left on device"). It can wrap almost any git operation (usually a
		// failed lock-file write on `add`), so match the message first — before any class/code branch —
		// to keep it from being misread as a broken repository.
		case message.includes("StorageFull") || message.includes("No space left on device"):
			return GitErrorCode.NotEnoughDiskSpace;

		// A socket read or write outlived the timeout set in `crates/git/src/repo.rs`, over http or TLS.
		case (subset === 1 || subset === 2) && code === GIT_TIMEOUT:
			return GitErrorCode.NetworkTimeoutError;

		case subset === 3:
			return GitErrorCode.HealthcheckFailed;

		// A locked index (`.git/index.lock`, class=Index/10 + code=Locked/14) breaks the catalog even when
		// `repo.healthcheck()` finds no bad objects (Rust reports subset=1 then). Classify it as a healthcheck
		// failure: `RepositoryHealthcheckHandler` runs the healthcheck, which removes the lock, and marks the repo
		// broken (recovery modal, GES-80) only if the healthcheck fails too.
		case eq(10, 14):
			return GitErrorCode.HealthcheckFailed;

		// A stash cut off halfway leaves `.git/refs/stash.lock` (class=OS/2 + code=Locked/14), which libgit2 never
		// expires: every later sync fails on it. The same healthcheck removes it, as it removes the index lock.
		case code === 14 && libgit2Text(message).includes("refs/stash.lock"):
			return GitErrorCode.HealthcheckFailed;

		case eq(20, 11):
		case eq(13, 20):
		case eq(20, 13):
			return GitErrorCode.CheckoutConflictError;

		// `Checkout::checkout` refuses a branch another worktree has on its HEAD before writing a file (#987).
		case subset === BRANCH_CHECKED_OUT_ELSEWHERE_SUBSET:
			return GitErrorCode.BranchCheckedOutElsewhere;

		// A file whose name is invalid on Windows (e.g. contains `"`) can be committed from Linux/macOS,
		// then breaks `checkout`/`stash` for anyone on Windows (NM-24).
		case klass === 20 && message.includes("cannot checkout to invalid path"):
			return GitErrorCode.InvalidPathError;

		case eq(22, 22):
		case eq(22, 11):
		case eq(22, 0):
		case eq(10, 8):
		case eq(10, 10):
		case eq(19, 20):
			return GitErrorCode.MergeConflictError;

		// A server-side hook refused the push: GitLab push rules or a protected branch
		// ("pre-receive hook declined"), GitHub ("protected branch hook declined"), a custom hook.
		// Rust reports every rejected ref as class=Net/12 + code=Invalid/21 — the same pair as
		// other push failures — so the status text is the only thing that tells them apart.
		// The status line comes from `push_update_reference_callback` (crates/git/src/remote_callback.rs),
		// which libgit2 runs for push only, so a fetch can never land here — the server text Rust
		// appends after the status is not consulted.
		case klass === 12 &&
			splitRemoteMessage(message).status.startsWith("failed to push;") &&
			message.includes("hook declined"):
			return GitErrorCode.PushDeclinedByRemoteError;

		case eq(12, 19):
		case eq(12, 21):
			return GitErrorCode.GitPushError;

		case eq(4, 4):
			return GitErrorCode.AlreadyExistsError;

		case eq(4, 1):
		case eq(4, 3):
			return GitErrorCode.NotFoundError;

		// libgit2 GIT_ERROR_REPOSITORY (class 6) + GIT_ENOTFOUND (code -3, serialized as 3): there is
		// no git repository at the path at all — `.git` was deleted or never created. Left unclassified
		// the raw libgit2 text reached the modal as is, argument dump included ("could not find
		// repository at '<path>' Args: { repoPath … patterns … force … }").
		case eq(6, 3):
			return GitErrorCode.LocalRepositoryNotFoundError;

		case message.includes("unexpected http status code: 404") || code === 404:
			return GitErrorCode.RemoteRepositoryNotFoundError;

		case code === 401 || code === 403:
		case subset === LFS_SUBSET && message.startsWith(LFS_ACCESS_DENIED):
		case message.includes("too many redirects or authentication replays"):
		case message.includes("unexpected http status code: 403"):
		case message.includes("unexpected http status code: 401"):
			return GitErrorCode.NotAuthorizedError;

		case [413, 431, 422].some((c) => code === c || message.includes(`unexpected http status code: ${c}`)):
			return GitErrorCode.ContentTooLargeError;

		// The request never left the browser: no DNS, no route, or CORS refused it. Without this
		// the synthetic 999 would fall into `code > 299` below and be reported as a git-server
		// error, sending people off to re-issue tokens for a problem in their own network.
		case subset === NETWORK_SUBSET && code === UNREACHABLE_STATUS:
			return GitErrorCode.NetworkConntectionError;

		case eq(34, 16):
		case eq(34, 0):
		case code > 299:
			return GitErrorCode.HttpError;

		case eq(4, 9):
		case eq(4, 11):
			return GitErrorCode.PushRejectedError;

		case eq(2, 0) && !message.includes("file"):
			return GitErrorCode.NetworkConntectionError;

		// TLS failures of libgit2's native transport: OpenSSL reports them as class SSL (16), a certificate refused is
		// GIT_ECERTIFICATE (-17) and a timeout GIT_TIMEOUT (-37) whatever the class. The wasm transport reports
		// every failure to reach the server as the synthetic status above.
		case klass === GIT_CLASS_SSL:
		case subset !== NETWORK_SUBSET && (code === GIT_CERTIFICATE || code === GIT_TIMEOUT):
			return GitErrorCode.NetworkConntectionError;

		// Specific push net-codes (12/19, 12/21) are matched above; any other transport failure,
		// whichever class the platform reported it under, is a connectivity issue.
		case isTransportFailure(klass, message):
		// Failures to reach the server that libgit2 reports as net (12), OS (2) — the socket stream and WinHTTP append
		// the system's words — or http (34) errors.
		case (klass === GIT_CLASS_NET || klass === GIT_CLASS_OS || klass === GIT_CLASS_HTTP) &&
			isNetworkWording(message, klass === GIT_CLASS_NET):
			return GitErrorCode.NetworkConntectionError;

		case klass === 14 && message.includes("does not exist in the given tree"):
			return GitErrorCode.FileNotFoundError;

		case command === "clone" &&
			(message.includes("indexer callback") ||
				message.includes("aborted") ||
				message.includes("no error") ||
				message.includes("-1")):
			return GitErrorCode.CancelledOperation;

		case eq(JSErrorClass, 19):
			return GitErrorCode.NetworkConntectionError;

		default:
			return undefined;
	}
};

// biome-ignore lint/suspicious/noExplicitAny: idc
export const makeData = (code: GitErrorCode, rawCode: number, message = ""): any => {
	switch (code) {
		case GitErrorCode.PushDeclinedByRemoteError:
			return splitRemoteMessage(message);

		case GitErrorCode.PushRejectedError:
			return {
				reason: "not-fast-forward",
			};

		case GitErrorCode.HttpError:
			return {
				statusCode: rawCode > 99 ? rawCode : -1,
			};

		case GitErrorCode.RemoteRepositoryNotFoundError:
			return {
				statusCode: rawCode > 99 ? rawCode : 404,
			};

		default:
			return undefined;
	}
};
